import React, { useEffect, useState } from 'react';
import { 
  IonPage, 
  IonHeader, 
  IonToolbar, 
  IonTitle, 
  IonContent, 
  IonItem, 
  IonLabel, 
  IonButtons, 
  IonBackButton, 
  IonList, 
  IonToggle, 
  useIonToast,
  IonListHeader,
  IonNote,
  IonBadge,
  IonButton,
  IonIcon
} from '@ionic/react';
import { syncOutline } from 'ionicons/icons';
import { manualCheckForUpdates } from '../components/UpdateNotification';

const Settings: React.FC = () => {
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(false);
  const [notificationStatus, setNotificationStatus] = useState<string>('');
  const [presentToast] = useIonToast();

  useEffect(() => {
    // Kontrollera notifikationstillstånd
    if ('Notification' in window) {
      const permission = Notification.permission;
      const hasDeclined = localStorage.getItem('notificationPromptDeclined') === 'true';
      console.log('Current notification permission:', permission, 'User declined:', hasDeclined);
      
      // Toggle är bara aktiverad om både permission är granted OCH användaren inte har valt att stänga av
      setNotificationsEnabled(permission === 'granted' && !hasDeclined);
      setNotificationStatus(permission);
    }
  }, []);

  const handleNotificationToggle = async (checked: boolean) => {
    if (checked) {
      // Aktivera notifikationer - försök alltid fråga användaren
      try {
        const permission = await Notification.requestPermission();
        console.log('Notification permission response:', permission);
        
        if (permission === 'granted') {
          localStorage.removeItem('notificationPromptDeclined');
          setNotificationsEnabled(true);
          setNotificationStatus('granted');
          presentToast({
            message: 'Notifikationer aktiverade',
            duration: 2000,
            color: 'success'
          });
          // Ladda om sidan för att trigga FCM token-registrering
          setTimeout(() => window.location.reload(), 1000);
        } else if (permission === 'denied') {
          setNotificationsEnabled(false);
          setNotificationStatus('denied');
          presentToast({
            message: 'Notifikationer är blockerade. Ändra i webbläsarens inställningar (klicka på hänglåset i adressfältet).',
            duration: 5000,
            color: 'danger',
            position: 'top'
          });
        } else {
          setNotificationsEnabled(false);
          setNotificationStatus(permission);
          presentToast({
            message: 'Notifikationer nekades. Du kan ändra detta i webbläsarens inställningar.',
            duration: 4000,
            color: 'warning',
            position: 'top'
          });
        }
      } catch (error) {
        console.error('Fel vid aktivering av notifikationer:', error);
        setNotificationsEnabled(false);
      }
    } else {
      // Avaktivera notifikationer (markera som declined)
      localStorage.setItem('notificationPromptDeclined', 'true');
      setNotificationsEnabled(false);
      presentToast({
        message: 'Du kommer inte frågas om notifikationer igen. Ändra i webbläsarens inställningar för att återaktivera.',
        duration: 4000,
        color: 'medium'
      });
    }
  };

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonBackButton />
          </IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Inställningar</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonListHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Notifikationer</IonLabel>
          </IonListHeader>
          <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <h2>Push-notifikationer</h2>
              <p>Aktivera för att få meddelanden när det händer något viktigt</p>
              {notificationStatus === 'denied' && (
                <div style={{marginTop: '8px', padding: '12px', backgroundColor: '#fee', borderRadius: '8px', border: '1px solid #fcc'}}>
                  <p style={{margin: 0, fontWeight: 'bold', color: '#c33'}}>⚠️ Notifikationer är blockerade</p>
                  <p style={{margin: '8px 0 0 0', fontSize: '0.9em', color: '#666'}}>
                    Du måste manuellt aktivera notifikationer i webbläsaren:
                  </p>
                  <ol style={{margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '0.9em', color: '#666'}}>
                    <li>Klicka på <strong>hänglåset/informations-ikonen</strong> i adressfältet (längst upp till vänster)</li>
                    <li>Klicka på <strong>Webbplatsinställningar</strong> eller <strong>Behörigheter</strong></li>
                    <li>Leta upp <strong>Notifikationer</strong></li>
                    <li>Ändra till <strong>Tillåt</strong></li>
                    <li>Ladda om sidan och aktivera toggle:n igen</li>
                  </ol>
                </div>
              )}
              {notificationStatus === 'granted' && (
                <IonBadge color="success" style={{marginTop: '8px'}} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Aktiverad</IonBadge>
              )}
              {notificationStatus === 'default' && (
                <IonNote placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <p style={{marginTop: '8px'}}>Du har inte valt om du vill få notifikationer ännu</p>
                </IonNote>
              )}
            </IonLabel>
            <IonToggle 
              checked={notificationsEnabled} 
              onIonChange={(e) => handleNotificationToggle(e.detail.checked)}
              disabled={!('Notification' in window)}
              placeholder={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </IonItem>
        </IonList>

        <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonListHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Appversion</IonLabel>
          </IonListHeader>
          <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <h2>Version</h2>
              <p>1.1.14</p>
            </IonLabel>
          </IonItem>
          <IonItem button onClick={manualCheckForUpdates} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonIcon icon={syncOutline} slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <h2>Kolla efter uppdateringar</h2>
              <p>Kontrollera om det finns en ny version av appen</p>
            </IonLabel>
          </IonItem>
        </IonList>
      </IonContent>
    </IonPage>
  );
};

export default Settings;
